
import { Component, HostBinding, OnInit, OnDestroy, AfterViewInit, ViewChild } from '@angular/core'
import { ActivatedRoute, Router } from '@angular/router'
import { Subject } from 'rxjs'
import { takeUntil } from 'rxjs/operators'
import { GrowthToolkitClient, ApplicationArea } from 'src/app/shared/services/ppv.api'
import { LeaderService } from '../../services/leader.service'
import { AuthManager } from 'src/app/shared/auth/AuthManager'
import { MatDialog } from '@angular/material/dialog'
import { MatTableDataSource } from '@angular/material/table'
import { MatPaginator } from '@angular/material/paginator'
import { MatSort } from '@angular/material/sort'
import { AssignGoalsModalComponent } from './assign-goals-modal/assign-goals-modal'
import { ViewTaskModalComponent } from '../tasks/view-task-modal/view-task-modal.component'
import { W } from '@angular/cdk/keycodes'
import { max } from 'moment'
import { GoalDetailsComponent } from './goal-details/goal-details.component'
import { CreateNewTaskComponent } from '../tasks/create-new-task/create-new-task.component'

@Component({
   selector: 'leader-toolkit-goals',
   templateUrl: './goals-page.component.html',
   styleUrls: ['./goals-page.component.scss'],
   standalone: false,
})
export class LeaderToolkitGoalsPage implements OnInit, OnDestroy, AfterViewInit {
   @HostBinding('class.full-flex-height') fullFlexHeight = true

   selectedHospitals: any[] = []
   selectedStatuses: string[] = []
   selectedLeadershipAttentionFlags: string[] = []
   selectedRegionalManagers: string[] = []
   regionalManagerList: string[] = []
   isRmRole: boolean = false
   leadershipFilterOptions = [
      { key: 'NO_ATTENTION_NEEDED', label: 'No Attention Needed' },
      { key: 'MONITOR', label: 'Monitor' },
      { key: 'ATTENTION_REQUIRED', label: 'Attention Required' },
   ]
   priorityPracticeOnly: boolean = false
   selectedGoals: any[] = []
   hospitalList: any[] = [];
   showViewTaskModal = false;
   selectedTaskData: any = null;
   globalFilter: string = ''
   displayedColumns: string[] = ['hospitalName', 'status','waitingOn', 'goalName', 'days', 'startDate', 'tasks', 'action']
   //displayedColumns: string[] = ['hospitalName', 'status', 'goalName', 'description', 'startDate', 'days', 'tasks', 'action']

   dataSource!: MatTableDataSource<any>
   isLoading: boolean = true;

   // ViewChild references
   @ViewChild(MatSort) sort!: MatSort;
   @ViewChild(MatPaginator) paginator!: MatPaginator;
   goalStatus: any
   goalListCountData: any
   selectedQuarter: any = 'default';
   quarterList: any
   hasLoadedGoals = false;


   @ViewChild(MatSort) set matSort(ms: MatSort) {
      this.sort = ms;
      this.setDataSourceAttributes();
   }

   @ViewChild(MatPaginator) set matPaginator(mp: MatPaginator) {
      this.paginator = mp;
      this.setDataSourceAttributes();
   }



   private setDataSourceAttributes(): void {
      if (this.dataSource && this.paginator && this.sort) {
         this.dataSource.paginator = this.paginator;
         this.dataSource.sort = this.sort;
      }
   }

   goalList: any;
   goalLeadershipByTargetId = new Map<number, string>();
   goalPriorityByHospitalId = new Map<number, boolean>();
   goalDictionaryData: any;
   collapsedGroupKeys = new Set<string>()
   isGroupedView = true;
   // Remembers the last explicit "sort by Hospital" direction independently of
   // MatSort's single active column, so that sorting some other column (e.g.
   // Status) reorders rows within each hospital's block without discarding the
   // hospital group order the user already chose.
   private hospitalGroupSortDirection: 'asc' | 'desc' | '' = ''

   constructor(private growthToolkitClient: GrowthToolkitClient,
      private dialog: MatDialog, public router: Router, private route: ActivatedRoute, public leaderService: LeaderService, private authMgr: AuthManager) { }

   // Search deep-link: set in ngOnInit, consumed once after goal list loads.
   // The incoming id is expected to be a goalTargetId — BE will be updated
   // so that usp_GlobalSearch returns GoalTargetId in the Id field for goal
   // hits (today it returns GoalDictionary.GoalId). Once that lands, the
   // popup will resolve correctly with zero further FE changes.
   private pendingSearchHighlight: { id: number } | null = null
   private destroy$ = new Subject<void>()

   ngOnInit(): void {
      this.dataSource = new MatTableDataSource<any>([]);
      this.detectUserRole();
      this.getGoalList(this.getSelectedQuarterName());
      this.getOpsLeadershipFlags(this.getSelectedQuarterName());
      this.qtrList();
      this.getHospitalList();
      this.getStatusFilterData();
      this.getGoalDictionary();

      // Pick up the search-highlight intent from NavigationExtras.state set
      // by the search results page. We use in-memory state (NOT query
      // params) so the destination URL stays clean. After consuming, we
      // strip the marker from history.state so a hard refresh doesn't
      // accidentally re-open the popup.
      const state: any = (typeof history !== 'undefined' && history.state) || {}
      const sh = state.searchHighlight
      const id = Number(sh?.id)
      if (id && Number.isFinite(id) && id > 0) {
         this.pendingSearchHighlight = { id }
         try {
            const cleaned = { ...state }
            delete cleaned.searchHighlight
            history.replaceState(cleaned, '')
         } catch { /* non-browser env — ignore */ }
      }

      // Check for goalTargetId query param in URL (deep link from email notification)
   this.route.queryParams.pipe(takeUntil(this.destroy$)).subscribe(params => {
      const goalTargetId = params['goalTargetId']
      if (goalTargetId && !this.pendingSearchHighlight) {
         const id = Number(goalTargetId)
         if (Number.isFinite(id) && id > 0) {
            this.pendingSearchHighlight = { id }
            this.router.navigate([], { queryParams: { goalTargetId: null }, queryParamsHandling: 'merge', replaceUrl: true })
         }
      }
   })

      this.leaderService.searchHighlightEvent
         .pipe(takeUntil(this.destroy$))
         .subscribe(({ id: highlightId }) => {
            this.dialog.open(GoalDetailsComponent, { data: highlightId });
         });
   }

   ngOnDestroy(): void {
      this.destroy$.next();
      this.destroy$.complete();
   }

   private detectUserRole(): void {
      this.authMgr.getCurrentUser().subscribe((user) => {
         const appRole: any = user?.roles?.find((r: any) => r?.application === ApplicationArea.LTK && r?.roleName !== 'LeadershipToolKit')
         const roleName = appRole?.roleName || ''
         this.isRmRole = roleName === 'RM' || roleName === 'LTKRM'
      })
   }

   /**
    * Resolve the search-highlight intent: flash the matching row and open
    * GoalDetailsComponent using the exact same call as updateStatus(row).
    * Safe no-op when no row matches (e.g. wrong quarter, filtered out).
    */
   private applyPendingSearchHighlight(): void {
      const pending = this.pendingSearchHighlight
      if (!pending) return
      // Consume immediately so this only runs once per navigation.
      this.pendingSearchHighlight = null

      setTimeout(() => {
         const el = document.getElementById('search-row-goal-' + pending.id)
         if (el) {
            try {
               el.scrollIntoView({ behavior: 'smooth', block: 'center' })
            } catch { /* older browsers — ignore */ }
            el.classList.add('search-flash')
            setTimeout(() => el.classList.remove('search-flash'), 2500)
         }
         // 1:1 with the in-page "view goal" flow (updateStatus(row) opens
         // GoalDetailsComponent with row.goalTargetId — see line ~608).
         const dialogRef = this.dialog.open(GoalDetailsComponent, {
            data: pending.id,
         })
         dialogRef.afterClosed().subscribe((result) => {
            if (result == 'success') {
               this.getGoalList(this.getSelectedQuarterName())
            }
         })
      }, 0)
   }

   ngAfterViewInit(): void {
      this.dataSource.paginator = this.paginator;
      this.dataSource.sort = this.sort;
   }

   getHospitalList() {
      this.leaderService.getHospitalList().subscribe((res: any) => {
         this.hospitalList = res?.data;
      });
   }

   getStatusFilterData() {
      this.leaderService.geGoalStatusFilterData("GoalStatus").subscribe((res: any) => {
         const statuses = res?.data?.GoalStatus || []
         this.goalStatus = statuses.map((statusItem: any) => {
            const rawValue = (typeof statusItem === 'string')
               ? statusItem
               : (statusItem?.value ?? statusItem?.name ?? '')

            return {
               value: rawValue,
               label: this.getStatusLabel(rawValue),
            }
         })
      });
   }


   qtrList() {
      this.leaderService.getQtrList().subscribe((res: any) => {
         this.quarterList = res.data || [];
         this.selectedQuarter = this.getCurrentQuarter();
      })
   }

   applyQtr() {
      // If quarter is cleared from UI, default back to current quarter.
      if (!this.selectedQuarter) {
         this.selectedQuarter = this.getCurrentQuarter();
      }
      this.getGoalList(this.getSelectedQuarterName());
      this.getOpsLeadershipFlags(this.getSelectedQuarterName());
   }

   private getCurrentQuarter(): any {
      return this.quarterList?.find((q: any) => q?.currentQuarter) || null;
   }

   private getCurrentQuarterName(): string {
      return this.getCurrentQuarter()?.quarterName || 'default';
   }

   private getSelectedQuarterName(): string {
      if (!this.selectedQuarter) {
         return this.getCurrentQuarterName();
      }
      if (typeof this.selectedQuarter === 'string') {
         return this.selectedQuarter || this.getCurrentQuarterName();
      }
      return this.selectedQuarter.quarterName || this.getCurrentQuarterName();
   }

   getGoalList(selectedQuarter?: any) {
      this.isLoading = true;
      this.leaderService.getGoalList(selectedQuarter).subscribe((res: any) => {
         if (!res || !res.data) {
               this.goalList = []

               this.dataSource.data = []
               this.goalListCountData = {}
               this.goalCounts = this.buildGoalCounts()
               this.isLoading = false;
               this.hasLoadedGoals = true;
               return;
            }
         this.goalListCountData = res.data;;
         this.goalCounts = this.buildGoalCounts(res.data);
         this.goalList = this.attachLeadershipFlags(res.data.goalSummaries || []);
         this.extractRegionalManagers(this.goalList);
         this.goalData(this.goalList);
         this.isLoading = false;
         this.hasLoadedGoals = true;
         this.applyPendingSearchHighlight();
      }, (error: any) => {
         console.error('Error fetching goal list:', error);
         this.isLoading = false;
         this.hasLoadedGoals = true;
      });
   }

   getGoalDictionary() {
      this.leaderService.getGoalDictionary().subscribe((res: any) => {
         this.goalDictionaryData = res.data;
      });
   }

   private extractRegionalManagers(goalList: any[]): void {
      const rmSet = new Set<string>()
      ;(goalList || []).forEach((row: any) => {
         const rm = (row?.rmName || '').toString().trim()
         if (rm) {
            rmSet.add(rm)
         }
      })
      this.regionalManagerList = Array.from(rmSet).sort()
   }

   goalData(goalList: any) {
      console.log('Received goal list:', goalList);
      this.dataSource.data = this.isGroupedView ? this.buildGroupedRows(goalList) : (goalList || [])
      this.dataSource.filterPredicate = (row: any, filter: string) => {
         let filterObj: any = {}
         try {
            filterObj = JSON.parse(filter)
         } catch (e) {
            filterObj = { search: filter }
         }

         if (row?.isGroup) {
            return row.rows?.some((r: any) => this.matchesGoalFilter(r, filterObj))
         }

         return this.matchesGoalFilter(row, filterObj)
      }

      this.dataSource.sortData = (data: any[], sort: MatSort) => {
         if (!sort.active || sort.direction === '') {
            return data
         }

         const comparator = (a: any, b: any) => {
            const valueA = this.getSortValue(a, sort.active)
            const valueB = this.getSortValue(b, sort.active)
            const cmp = valueA < valueB ? -1 : valueA > valueB ? 1 : 0
            return sort.direction === 'asc' ? cmp : -cmp
         }

         if (!this.isGroupedView) {
            return data.slice().sort(comparator)
         }

         // Grouped view: split into hospital blocks (group header + its rows).
         // Sorting by "hospitalName" reorders the blocks themselves — sorting
         // each group's rows by their own (identical) hospitalName was always
         // a no-op. Sorting by any other column reorders the rows within each
         // block, keeping every row with its hospital.
         const blocks: { group: any; rows: any[] }[] = []
         let current: { group: any; rows: any[] } | null = null
         data.forEach((row: any) => {
            if (row?.isGroup) {
               current = { group: row, rows: [] }
               blocks.push(current)
            } else if (current) {
               current.rows.push(row)
            }
         })

         if (sort.active === 'hospitalName') {
            this.hospitalGroupSortDirection = sort.direction as 'asc' | 'desc'
            blocks.sort((a, b) => comparator(a.group, b.group))
         } else {
            // Keep the hospital blocks in whatever order the user last chose
            // by sorting the Hospital column — a sort on another column is a
            // secondary sort applied within each block, not a replacement for
            // the group order.
            if (this.hospitalGroupSortDirection) {
               const dir = this.hospitalGroupSortDirection === 'asc' ? 1 : -1
               blocks.sort((a, b) => {
                  const valueA = this.getSortValue(a.group, 'hospitalName')
                  const valueB = this.getSortValue(b.group, 'hospitalName')
                  const cmp = valueA < valueB ? -1 : valueA > valueB ? 1 : 0
                  return dir * cmp
               })
            }
            blocks.forEach((block) => block.rows.sort(comparator))
         }

         const result: any[] = []
         blocks.forEach((block) => {
            result.push(block.group)
            result.push(...block.rows)
         })
         return result
      }

      this.applyFilters()
   }

   matchesGoalFilter(row: any, filterObj: any): boolean {
      const search = (filterObj.search || '').toString().toLowerCase()
      const hospitalPracticeSKeys = (filterObj.hospitalPracticeSKeys || []).map((k: any) => (k || '').toString().trim()).filter((k: string) => !!k)
      const hospitalNames = (filterObj.hospitalNames || []).map((n: any) => (n || '').toString().toLowerCase().trim()).filter((n: string) => !!n)
      const statuses = (filterObj.statuses || []).map((s: any) => (s || '').toString().toLowerCase())
      const goalNames = (filterObj.goalNames || []).map((g: any) => (g || '').toString().toLowerCase()).filter((g: string) => !!g)
      const leadershipFlags = (filterObj.leadershipFlags || []).map((flag: any) => (flag || '').toString())
      const regionalManagers = (filterObj.regionalManagers || []).map((rm: any) => (rm || '').toString().toLowerCase().trim()).filter((rm: string) => !!rm)
      const priorityOnly = !!filterObj.priorityOnly
      const rowStatus = (row.status || '').toString().toLowerCase()
      const includesDeletedStatus = statuses.includes('deleted')

      if (rowStatus === 'deleted' && !includesDeletedStatus) {
         return false
      }

      const matchesSearch = !search || (
         (row.hospitalName || '').toString().toLowerCase().includes(search) ||
         (row.goalName || '').toString().toLowerCase().includes(search) ||
         (row.description || '').toString().toLowerCase().includes(search)
      )

      const rowPracticeSKey = (row?.practiceSKey ?? row?.hospitalPracticeSKey ?? row?.hospitalId ?? row?.userId ?? '').toString().trim()
      const rowHospitalName = (row.hospitalName || '').toString().toLowerCase().trim()
      const matchesHospital = (hospitalPracticeSKeys.length === 0 && hospitalNames.length === 0)
         || hospitalPracticeSKeys.includes(rowPracticeSKey)
         || hospitalNames.includes(rowHospitalName)
      const matchesStatus = statuses.length === 0 || statuses.includes(rowStatus)
      const matchesGoal = goalNames.length === 0 || goalNames.includes((row.goalName || '').toString().toLowerCase())
      const matchesLeadership = leadershipFlags.length === 0
         || leadershipFlags.includes(this.normalizeLeadershipAttentionKey(row.leadershipAttentionRequired))
      const matchesPriority = !priorityOnly || row.isCritical === true
      const rowRmName = (row?.rmName || '').toString().toLowerCase().trim()
      const matchesRegionalManager = regionalManagers.length === 0 || regionalManagers.includes(rowRmName)

      return matchesSearch && matchesHospital && matchesStatus && matchesGoal && matchesLeadership && matchesPriority && matchesRegionalManager
   }

   getTotalCount(taskInfos: any[]): number {
      return Array.isArray(taskInfos) ? taskInfos.length : 0;
   }

   getCompleteCount(taskInfos: any[]): number {
      if (!Array.isArray(taskInfos)) {
         return 0;
      }
      return taskInfos.filter((task) => (task?.taskStatus || '').toString().toLowerCase() === 'complete').length;
   }

   buildGroupedRows(goalList: any[]): any[] {
      const groups = new Map<string, { hospitalName: string; hospitalId?: any; rows: any[] }>();
      const rows = goalList || []

      rows.forEach((row: any) => {
         const name = (row.hospitalName || 'No Hospital Assigned').toString().trim() || 'No Hospital Assigned'
         const key = name.toLowerCase()
         if (!groups.has(key)) {
            groups.set(key, {
               hospitalName: name,
               hospitalId: row.hospitalId ?? row.userId,
               rows: [],
            })
         }
         groups.get(key)!.rows.push(row)
      })

      const groupedRows: any[] = []
      groups.forEach((group) => {
         const groupKey = this.getGroupKey(group.hospitalName)
         groupedRows.push({
            isGroup: true,
            hospitalName: group.hospitalName,
            hospitalId: group.hospitalId,
            groupKey,
            rows: group.rows,
         })
         group.rows.forEach((row) => {
            row.groupKey = groupKey
            groupedRows.push(row)
         })
      })

      return groupedRows
   }

   getGroupKey(name: string): string {
      return (name || 'No Hospital Assigned').toString().trim().toLowerCase()
   }

   toggleGroup(row: any): void {
      const key = row?.groupKey || this.getGroupKey(row?.hospitalName)
      if (this.collapsedGroupKeys.has(key)) {
         this.collapsedGroupKeys.delete(key)
      } else {
         this.collapsedGroupKeys.add(key)
      }
   }

   isGroupCollapsed(row: any): boolean {
      const key = row?.groupKey || this.getGroupKey(row?.hospitalName)
      return this.collapsedGroupKeys.has(key)
   }

   isRowHidden(row: any): boolean {
      if (!this.isGroupedView) return false
      if (row?.isGroup) return false
      const key = row?.groupKey || this.getGroupKey(row?.hospitalName)
      return this.collapsedGroupKeys.has(key)
   }

   getSortValue(row: any, column: string): any {
      switch (column) {
         case 'hospitalName':
            return (row.hospitalName || '').toString().toLowerCase()
         case 'status':
            return (row.status || '').toString().toLowerCase()
         case 'goalName':
            return (row.goalName || '').toString().toLowerCase()
         case 'startDate':
            return new Date(row.startDate || 0).getTime()
         case 'days':
            return Number(row.days) || 0
         default:
            return (row[column] || '').toString().toLowerCase()
      }
   }

   isGroupRow = (_: number, row: any) => row?.isGroup === true

   toggleGroupedView(): void {
      this.isGroupedView = !this.isGroupedView
      this.dataSource.data = this.isGroupedView ? this.buildGroupedRows(this.goalList || []) : (this.goalList || [])
      if (this.dataSource && this.dataSource.sort) {
         this.dataSource.sort = this.sort
      }
      this.paginator?.firstPage()
   }

   private buildGoalCounts(countsSource: any = {}) {
      const counts = countsSource?.goalCounts || countsSource || {}
      const {
         goalLive = 0,
         goalWatingOnMe = 0,
         goalWaitingOnMe = 0,
         goalWaitingOnOther = 0,
         settleGoal = 0,
         goalAssigned = 0,
         pending = 0,
         pendingHMRM = 0,
      } = counts

      const waitingOnMe = goalWaitingOnMe || goalWatingOnMe
      const waitingOnOther = goalWaitingOnOther || pending
      const settledGoal = settleGoal || pendingHMRM

      return [
         { count: goalLive, label: 'Goals Live', icon: 'check_circle', color: 'green' },
         { count: waitingOnMe || goalAssigned, label: 'Goals Waiting On Me', icon: 'domain', color: 'blue' },
         { count: waitingOnOther, label: 'Goals Waiting On Other', icon: 'work', color: 'orange' },
         { count: settledGoal, label: 'Stale Goals', icon: 'manage_accounts', color: 'red' },
      ]
   }

   goalCounts = this.buildGoalCounts()


   get uniqueGoals() {
      return this.goalList?.map((r: any) => r.goalName).filter((v: any, i: number, a: any[]) => a.indexOf(v) === i)
   }

   applyFilters() {
      // Extract practiceSKeys / names from selectedHospitals (objects or strings)
      const hospitalPracticeSKeys: string[] = []
      const hospitalNames: string[] = []
      ;(this.selectedHospitals || []).forEach((h: any) => {
         if (typeof h === 'string') {
            hospitalNames.push(h)
         } else if (h.practiceSKey !== undefined && h.practiceSKey !== null) {
            hospitalPracticeSKeys.push(h.practiceSKey.toString())
         } else if (h.displayName) {
            hospitalNames.push(h.displayName)
         } else if (h.practiceName) {
            hospitalNames.push(h.practiceName)
         }
      })

      const goalNames = (this.selectedGoals || []).map((g: any) => {
         if (typeof g === 'string') return g
         return g?.name || ''
      }).filter((n: string) => !!n)

      const filterObj = {
         search: this.globalFilter || '',
         hospitalPracticeSKeys: hospitalPracticeSKeys,
         hospitalNames: hospitalNames,
         statuses: this.selectedStatuses || [],
         goalNames: goalNames,
         leadershipFlags: this.selectedLeadershipAttentionFlags || [],
         regionalManagers: this.selectedRegionalManagers || [],
         priorityOnly: this.priorityPracticeOnly,
      };
      this.dataSource.filter = JSON.stringify(filterObj);
      if (this.dataSource.paginator) {
         this.dataSource.paginator.firstPage();
      }
   }

   resetFilters() {
      this.selectedHospitals = []
      this.selectedStatuses = []
      this.selectedLeadershipAttentionFlags = []
      this.selectedRegionalManagers = []
      this.priorityPracticeOnly = false
      this.selectedGoals = []
      this.globalFilter = ''
      this.applyFilters()
      if (this.dataSource && this.dataSource.paginator) {
         this.dataSource.paginator.firstPage();
      }
   
   }


   goalDetails(row: any) {
      this.router.navigate(['/leader-goal-toolkit-details', row.hospitalId]);
   }

   openAssignModal() {
      const dialogRef = this.dialog.open(AssignGoalsModalComponent, {
         data: {
            uniqueHospitals: this.hospitalList,
            quarterList: this.quarterList  ,
             autoFocus: false,
             Width: '800px',
             maxWidth: '800px',
             height: '600px',
             maxHeight: '600px',
             innerWidth: '800px',
             maxInnerWidth: '800px',
             innerHeight: '600px'
         }
      });
       dialogRef.afterClosed().subscribe(result => {
           if (result === 'success') {
               this.getGoalList(this.getSelectedQuarterName());
            }
         });
   }
   openViewTask(taskId: any) {
      const dialogRef = this.dialog.open(ViewTaskModalComponent, {
          data: {
            taskId: taskId,
            page: 'goals'
         },
      });
      dialogRef.afterClosed().subscribe(result => {
         if (result?.success) {
            this.getGoalList(this.getSelectedQuarterName());
         }
      });
   }
   getStatusClass(status: string): string {
      switch (status) {
         case 'Past Due':
            return 'badge-red'
         case 'Complete':
         case 'Completed':
            return 'badge-green'
         case 'Pending':
            return 'badge-blue'
         default:
            return 'badge-blue'
      }
   }

   getStatusLabel(status: string): string {
      const normalized = (status || '').toString().trim().toUpperCase();
      switch (normalized) {
         case 'PENDING_VP_REVIEW':
            return 'VP/DO Review';
         case 'PENDING_RM_REVIEW':
            return 'RM Review';
         case 'DELETED':
            return 'Deleted';
          case 'COMPLETE':
         case 'COMPLETED':
            return 'Complete';
         case 'PENDING':
            return 'Pending';
         case 'LIVE':
            return 'Live';
         case 'ASSIGNED':
            return 'Assigned';
         default:
            return status || '';
      }
   }

   getWaitingOnLabel(waitingOn: string): string {
      if (!waitingOn) {
         return '';
      }

      const normalized = waitingOn
         .toString()
         .split(',')
         .map((value) => value.trim())
         .filter((value) => !!value)
         .map((value) => (value.toUpperCase() === 'LTKRM' ? 'RM' : value.toUpperCase()))

      const unique = Array.from(new Set(normalized))
         .filter((value) => value !== 'ADM')

      const order = ['VP', 'DO']
      unique.sort((a, b) => {
         const ai = order.indexOf(a)
         const bi = order.indexOf(b)
         if (ai === -1 && bi === -1) return a.localeCompare(b)
         if (ai === -1) return 1
         if (bi === -1) return -1
         return ai - bi
      })

      const formatted = unique.map((value) => (value === 'NONE' ? 'Nobody' : value))

      return formatted.join(', ')
   
   }

   getDaysSeverityClass(days: any, status?: string): string {
      const normalizedStatus = (status || '').toString().trim().toUpperCase();
      if (normalizedStatus === 'LIVE' || normalizedStatus === 'ASSIGNED' || normalizedStatus === 'DELETED') {
         return 'days-normal';
      }

      const daysNum = Number(days) || 0;
      if (daysNum >= 7) {
         return 'days-critical';
      } else if (daysNum > 3) {
         return 'days-warning';
      }
      return 'days-normal';
   }

   viewHospitalDetails(row: any) {
      const id = row?.hospitalId ?? row?.userId
      if (id !== undefined && id !== null) {
         this.router.navigate(['/leader-toolkit-hospital-editor', id]);
      }
   }
   // For description dialog
   descriptionDialogVisible = false;
   descriptionDialogText = '';

   showDescriptionDialog(desc: string) {
      this.descriptionDialogText = desc;
      this.descriptionDialogVisible = true;
   }

   updateStatus(row: any) {
          const dialogRef = this.dialog.open(GoalDetailsComponent, {
               data: row.goalTargetId
          })
          dialogRef.afterClosed().subscribe(result => {
             if (result == 'success') {
                 this.getGoalList(this.getSelectedQuarterName());
             }
          });
       }
 openCreateTask(row?: any) {
      const dialogRef = this.dialog.open(CreateNewTaskComponent, {
         data: {
            hospital: row || null,
            quarter: this.selectedQuarter || null,
            goal: row
               ? {
                    goalTargetId: row.goalTargetId ?? null,
                    goalName: row.goalName ?? null,
                 }
               : null,
         },
      })
      dialogRef.afterClosed().subscribe(result => {
         if (result === 'success') {
            this.getGoalList(this.getSelectedQuarterName())
         }
      })
   }

   private getOpsLeadershipFlags(quarterName: string): void {
      this.leaderService.getOpsHospitalData(quarterName).subscribe({
         next: (res: any) => {
            this.goalLeadershipByTargetId.clear();
            this.goalPriorityByHospitalId.clear();
            const hospitals = res?.data || [];
            hospitals.forEach((hospital: any) => {
               const hospitalId = Number(hospital?.hospitalId || 0);
               if (hospitalId > 0) {
                  this.goalPriorityByHospitalId.set(hospitalId, hospital?.isCritical === true);
               }
               const goals = hospital?.goals || [];
               goals.forEach((goal: any) => {
                  const targetId = Number(goal?.goalTargetId || 0);
                  const leadershipFlag = goal?.leadershipAttentionRequired;
                  if (targetId > 0 && leadershipFlag) {
                     this.goalLeadershipByTargetId.set(targetId, this.normalizeLeadershipAttention(leadershipFlag));
                  }
               });
            });

            if (Array.isArray(this.goalList) && this.goalList.length > 0) {
               this.goalList = this.attachLeadershipFlags(this.goalList);
               this.goalData(this.goalList);
            }
         },
      });
   }

   private attachLeadershipFlags(goalRows: any[]): any[] {
      return (goalRows || []).map((row: any) => {
         const targetId = Number(row?.goalTargetId || 0);
         const hospitalId = Number(row?.hospitalId ?? row?.userId ?? 0);
         return {
            ...row,
            leadershipAttentionRequired: targetId > 0 ? (this.goalLeadershipByTargetId.get(targetId) || '') : '',
            isCritical: hospitalId > 0 ? (this.goalPriorityByHospitalId.get(hospitalId) === true) : false,
         };
      });
   }

   togglePriorityPracticeFilter(): void {
      this.priorityPracticeOnly = !this.priorityPracticeOnly
      this.applyFilters()
   }

   private normalizeLeadershipAttention(value: any): string {
      const normalized = (value || '').toString().trim().toUpperCase().replace(/\s+/g, ' ');
      if (normalized === 'ATTENTION REQUIRED') return 'Attention Required';
      if (normalized === 'MONITOR') return 'Monitor';
      if (normalized === 'NO ATTENTION NEEDED' || normalized === 'NO ATTENSION REQUIRED' || normalized === 'NO ATTENTION REQUIRED') {
         return 'No Attention Needed';
      }
      return '';
   }

   private normalizeLeadershipAttentionKey(value: any): string {
      const label = this.normalizeLeadershipAttention(value);
      if (label === 'Attention Required') return 'ATTENTION_REQUIRED';
      if (label === 'Monitor') return 'MONITOR';
      if (label === 'No Attention Needed') return 'NO_ATTENTION_NEEDED';
      return '';
   }
}
